import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Star } from 'lucide-react';

import { formatDateMonthDay } from '../../utils/timeFormat';
const RatingsPage = () => {
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchRatings();
  }, []);

  const fetchRatings = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/client/ratings`, { headers });
      const data = await res.json();
      setRatings(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching ratings:', e);
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="ratings-page">
      <div className="max-w-4xl mx-auto px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/client/dashboard')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">Your Ratings</CardTitle>
            <p className="text-sm text-gray-500">
              Ratings you've given to talent after completed gigs
            </p>
          </CardHeader>
          <CardContent>
            {ratings.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No ratings yet. You can rate talent after completing a gig.
              </div>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div 
                    key={rating.id}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">{rating.talent_name}</h3>
                        <p className="text-sm text-gray-500">
                          {rating.venue_name} • {rating.gig_date && formatDateMonthDay(rating.gig_date)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {renderStars(rating.rating)}
                      </div>
                    </div>
                    {rating.performance_feedback && (
                      <div className="mt-2 p-3 bg-white rounded border border-gray-100">
                        <p className="text-sm text-gray-600">{rating.performance_feedback}</p>
                      </div>
                    )}
                    {rating.private_notes && (
                      <div className="mt-2 p-3 bg-amber-50 rounded border border-amber-100">
                        <p className="text-xs text-amber-700 font-medium mb-1">Private Note (visible to admin only)</p>
                        <p className="text-sm text-gray-600">{rating.private_notes}</p>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Rated on {rating.created_at && formatDateMonthDay(rating.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RatingsPage;
